
import React, { useEffect } from 'react';
import MainLayout from '../layouts/MainLayout';
import { Helmet } from 'react-helmet';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PortfolioDetailProps {
  title: string;
  subtitle: string;
  description: string;
  image: string;
  videoSrc?: string;
}

const portfolioItems: Record<string, PortfolioDetailProps> = {
  "martyna-filip": {
    title: "MARTYNA + FILIP",
    subtitle: "Pałac Konary. Ślub kościelny plenerowy",
    description: "Piękna ceremonia ślubna w plenerze i wesele w Pałacu Konary. Martyna i Filip zdecydowali się na organizację ślubu pod gołym niebem, otoczeni najbliższymi i piękną naturą. Reportaż z ich dnia zawiera zarówno wzruszające momenty przysięgi, jak i szaloną zabawę weselną.",
    image: "/lovable-uploads/c7eee809-8314-496e-9be8-ec4e5be22ec9.png",
    videoSrc: "https://player.vimeo.com/video/473843342"
  },
  "karolina-michal": {
    title: "KAROLINA + MICHAŁ",
    subtitle: "Wesele w Villa Julianna. Ceremonia w plenerze",
    description: "Karolina i Michał wybrali elegancką Villa Julianna na miejsce swojego wesela, a ceremonię zorganizowali w przepięknym ogrodzie. Ich dzień był pełen emocji, wzruszeń i radości, które mieliśmy przyjemność uwiecznić.",
    image: "/lovable-uploads/456f9d67-7c09-44bc-b716-a6e8270d986b.png",
    videoSrc: "https://player.vimeo.com/video/473843342"
  },
  "agnieszka-dawid": {
    title: "AGNIESZKA + DAWID",
    subtitle: "Wesele w starej stodole. Rustykalne dekoracje",
    description: "Agnieszka i Dawid postawili na klimatyczne wesele w starej, odrestaurowanej stodole. Rustykalne dekoracje, światełka i naturalne materiały stworzyły magiczną atmosferę na ich wyjątkowy dzień.",
    image: "/lovable-uploads/2d2f8ccd-db4a-4096-be62-3a8ef69e843d.png",
    videoSrc: "https://player.vimeo.com/video/473843342"
  },
  "aneta-adam": {
    title: "ANETA + ADAM",
    subtitle: "Pałac Żeleńskich Grodkowice",
    description: "Aneta i Adam wybrali na swój wielki dzień niezwykłe miejsce - Pałac Żeleńskich w Grodkowicach. Klasyczna elegancja tego miejsca idealnie współgrała z romantycznym charakterem ich uroczystości. Ceremonia odbyła się w zabytkowym kościele, a następnie goście przenieśli się do pałacu, gdzie bawili się do białego rana w otoczeniu historycznych wnętrz i pięknego parku.",
    image: "/lovable-uploads/2d2f8ccd-db4a-4096-be62-3a8ef69e843d.png",
    videoSrc: "https://player.vimeo.com/video/473843342"
  },
  "julia-mateusz": {
    title: "JULIA + MATEUSZ",
    subtitle: "Złoty Jar. Wesele w górskiej willi",
    description: "Julia i Mateusz zaprosili swoich gości do magicznego miejsca w górach - Złotego Jaru. Ta urokliwa, drewniana willa stała się tłem dla ich wyjątkowego dnia. Ceremonia z widokiem na góry, rustykalne dekoracje i domowa atmosfera stworzyły niepowtarzalny klimat tego wesela. Reportaż z tego dnia oddaje naturalne piękno zarówno miejsca, jak i emocji towarzyszących młodej parze.",
    image: "/lovable-uploads/456f9d67-7c09-44bc-b716-a6e8270d986b.png",
    videoSrc: "https://player.vimeo.com/video/473843342"
  }
};

const PortfolioDetail = () => {
  const { slug } = useParams();
  const item = slug ? portfolioItems[slug] : null;
  
  // Manually disable reCAPTCHA on this page to prevent errors
  useEffect(() => {
    // Remove recaptcha badge if it exists (to prevent errors)
    const badges = document.getElementsByClassName('grecaptcha-badge');
    if (badges.length > 0) {
      for (let i = 0; i < badges.length; i++) {
        const badge = badges[i] as HTMLElement;
        if (badge) {
          badge.style.visibility = 'hidden';
        }
      }
    }
    
    return () => {
      // Restore badges when component unmounts
      const badges = document.getElementsByClassName('grecaptcha-badge');
      if (badges.length > 0) {
        for (let i = 0; i < badges.length; i++) {
          const badge = badges[i] as HTMLElement;
          if (badge) {
            badge.style.visibility = 'visible';
          }
        }
      }
    };
  }, []);
  
  if (!item) {
    return (
      <MainLayout>
        <div className="container-custom pt-32 md:pt-48 pb-24 min-h-[70vh] flex flex-col items-center justify-center">
          <h1 className="text-3xl font-serif mb-4">Realizacja nie została znaleziona</h1>
          <p className="mb-8 text-elegant-600">Niestety, ta realizacja nie istnieje lub została przeniesiona.</p>
          <Button variant="navy" asChild>
            <Link to="/portfolio">Wróć do portfolio</Link>
          </Button>
        </div>
      </MainLayout>
    );
  }
  
  return (
    <MainLayout>
      <Helmet>
        <title>{`${item.title} | Czułe Klisze Portfolio`}</title>
        <meta name="description" content={item.description.substring(0, 160)} />
        <meta property="og:title" content={`${item.title} | Czułe Klisze Portfolio`} />
        <meta property="og:description" content={item.description.substring(0, 160)} />
        <meta property="og:type" content="website" />
        {slug && <meta property="og:url" content={`https://czuleklisze.pl/portfolio/${slug}`} />}
        <meta property="og:image" content={item.image} />
        {slug && <link rel="canonical" href={`https://czuleklisze.pl/portfolio/${slug}`} />}
      </Helmet>
      
      <div className="pt-32 md:pt-44 bg-white">
        <div className="container-custom">
          <Link to="/portfolio" className="inline-flex items-center text-elegant-600 hover:text-elegant-800 mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span>Wróć do portfolio</span>
          </Link>
          
          <h1 className="text-3xl md:text-5xl font-serif text-elegant-900 mb-4 tracking-tight">
            {item.title}
          </h1>
          <p className="text-xl text-elegant-600 mb-10 font-light">
            {item.subtitle}
          </p>
        </div>
      </div>
      
      <div className="py-8 md:py-12 bg-white">
        <div className="container-custom max-w-5xl">
          {item.videoSrc && (
            <div className="aspect-video w-full mb-10 shadow-lg rounded-md overflow-hidden">
              <iframe 
                src={item.videoSrc} 
                className="w-full h-full" 
                frameBorder="0" 
                allow="autoplay; fullscreen; picture-in-picture" 
                allowFullScreen 
                title={item.title}
              ></iframe>
            </div>
          )}
          
          <div className="prose prose-lg max-w-none">
            <p className="text-elegant-600 leading-relaxed whitespace-pre-line">
              {item.description}
            </p>
          </div>
        </div>
      </div>
      
      <div className="py-16 md:py-20 bg-elegant-50">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            <img src={item.image} alt={item.title} className="w-full aspect-[4/3] object-cover rounded-md shadow-md" />
            <img src={item.image} alt={item.title} className="w-full aspect-[4/3] object-cover rounded-md shadow-md" />
            <img src={item.image} alt={item.title} className="w-full aspect-[4/3] object-cover rounded-md shadow-md" />
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default PortfolioDetail;
